import type { Bitmap } from "./bitmap";

const CRC = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

function crc32(buf: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < buf.length; i++) c = CRC[(c ^ buf[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

function adler32(buf: Uint8Array): number {
  let a = 1;
  let b = 0;
  for (let i = 0; i < buf.length; i++) {
    a = (a + buf[i]) % 65521;
    b = (b + a) % 65521;
  }
  return ((b << 16) | a) >>> 0;
}

/** Minimal PNG encoder (stored deflate blocks). Good enough for test snapshots and QA images. */
export function encodePng(bm: Bitmap): Uint8Array {
  const raw = new Uint8Array((bm.w * 4 + 1) * bm.h);
  for (let y = 0; y < bm.h; y++) {
    raw[y * (bm.w * 4 + 1)] = 0;
    raw.set(bm.data.subarray(y * bm.w * 4, (y + 1) * bm.w * 4), y * (bm.w * 4 + 1) + 1);
  }
  const blocks: number[] = [0x78, 0x01];
  for (let i = 0; i < raw.length; i += 65535) {
    const len = Math.min(65535, raw.length - i);
    const last = i + len >= raw.length ? 1 : 0;
    blocks.push(last, len & 0xff, len >> 8, ~len & 0xff, (~len >> 8) & 0xff);
    for (let k = 0; k < len; k++) blocks.push(raw[i + k]);
  }
  const ad = adler32(raw);
  blocks.push(ad >>> 24, (ad >>> 16) & 0xff, (ad >>> 8) & 0xff, ad & 0xff);
  const chunk = (type: string, data: Uint8Array | number[]) => {
    const d = data instanceof Uint8Array ? data : new Uint8Array(data);
    const out = new Uint8Array(12 + d.length);
    const dv = new DataView(out.buffer);
    dv.setUint32(0, d.length);
    for (let i = 0; i < 4; i++) out[4 + i] = type.charCodeAt(i);
    out.set(d, 8);
    dv.setUint32(8 + d.length, crc32(out.subarray(4, 8 + d.length)));
    return out;
  };
  const ihdr = new Uint8Array(13);
  const dv = new DataView(ihdr.buffer);
  dv.setUint32(0, bm.w);
  dv.setUint32(4, bm.h);
  ihdr.set([8, 6, 0, 0, 0], 8);
  const parts = [new Uint8Array([137, 80, 78, 71, 13, 10, 26, 10]), chunk("IHDR", ihdr), chunk("IDAT", blocks), chunk("IEND", [])];
  const total = parts.reduce((s, p) => s + p.length, 0);
  const out = new Uint8Array(total);
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
}
